import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  apiCalls,
  useGetInspectorPortalAssignmentDetail,
} from "@/api/inspectorPortalQueries";
import {
  InspectorPortalAssignmentSummary,
  InspectorPortalUploadedFile,
  ViewingsDispatchInspector,
  ViewingsDispatchRequest,
} from "@/schemas";
import { toViewingRequest } from "../utils";
import { isAxiosError } from "axios";
import {
  Download,
  FileIcon,
  Link2,
  Loader2,
  MapPin,
  Maximize2,
  MoreVertical,
  Trash2,
} from "lucide-react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getLifecycleBadgeClassName } from "../dispatchActions";
import { RequestUpdater } from "../types";
import {
  getInspectionTypeLabel,
  getLifecycleStatusLabel,
} from "../viewingLabels";
import AssignmentActions, { InspectorUploadDialog } from "./AssignmentActions";
import {
  shouldShowWaitingOver48hAlert,
  WaitingOver48hAlertSection,
} from "./WaitingOver48hAlert";

const OFFER_OPEN_STATUSES = ["contacted"];
export type AssignmentDetailTab = "info" | "files";

function SimpleFact({
  label,
  value,
  prominent = false,
}: {
  label: ReactNode;
  value: ReactNode;
  prominent?: boolean;
}) {
  return (
    <div className="rounded-sm border border-border bg-card px-3 py-2.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd
        className={`mt-0.5 text-primary ${prominent ? "text-base font-semibold" : "text-sm font-medium"}`}
      >
        {value}
      </dd>
    </div>
  );
}

function LocationPanel({
  request,
}: {
  request: ViewingsDispatchRequest & {
    latitude?: number | null;
    longitude?: number | null;
  };
}) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as
    | string
    | undefined;
  const hasCoordinates =
    typeof request.latitude === "number" &&
    typeof request.longitude === "number";
  const fallbackDescriptionId = !hasCoordinates
    ? "viewings.detail.mapCoordinatesUnavailableDescription"
    : "viewings.detail.mapUnavailableDescription";

  useEffect(() => {
    if (!mapboxToken || !hasCoordinates || !mapContainerRef.current) return;

    const lngLat: [number, number] = [request.longitude!, request.latitude!];

    mapboxgl.accessToken = mapboxToken;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      center: lngLat,
      zoom: 12,
      style: "mapbox://styles/mapbox/streets-v12",
    });
    new mapboxgl.Marker().setLngLat(lngLat).addTo(mapRef.current);

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [hasCoordinates, mapboxToken, request.latitude, request.longitude]);

  return (
    <section className="flex h-full flex-col">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <FormattedMessage id="viewings.detail.location" />
      </h3>
      <div
        ref={mapContainerRef}
        data-testid="dispatch-map-preview"
        className="relative min-h-[260px] flex-1 overflow-hidden rounded-md border border-blue-100 bg-blue-100"
        style={{
          backgroundImage:
            "linear-gradient(rgba(56, 91, 108, 0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(56, 91, 108, 0.12) 1px, transparent 1px)",
          backgroundSize: "46px 46px",
        }}
      >
        {!mapboxToken || !hasCoordinates ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-50/50 to-sky-200/50" />
            <div className="absolute left-[53%] top-[36%] -translate-x-1/2 -translate-y-1/2">
              <div className="mb-1 rounded-sm bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground shadow-sm">
                {request.addressLine.split(",")[0]}
              </div>
              <div className="mx-auto flex size-7 items-center justify-center rounded-full border-2 border-background bg-primary shadow-md">
                <MapPin className="h-4 w-4 fill-background text-background" />
              </div>
            </div>
            <div className="absolute left-[29%] top-[64%] size-4 rounded-full border-2 border-background bg-blue-400 shadow-sm" />
            <div className="absolute left-[69%] top-[26%] size-4 rounded-full border-2 border-background bg-blue-400 shadow-sm" />
            <div className="absolute left-[73%] top-[72%] size-4 rounded-full border-2 border-background bg-blue-400 shadow-sm" />
            <div className="absolute inset-x-4 top-4 rounded bg-background/90 px-3 py-2 text-xs text-primary shadow-sm">
              <p className="font-semibold">
                <FormattedMessage id="viewings.detail.mapUnavailable" />
              </p>
              <p className="text-muted-foreground">
                <FormattedMessage id={fallbackDescriptionId} />
              </p>
            </div>
          </>
        ) : null}
        <div className="absolute bottom-2 left-2 flex items-center gap-3 rounded bg-background/90 px-3 py-1 text-xs text-primary shadow-sm">
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-primary" />
            <FormattedMessage id="viewings.detail.propertyMarker" />
          </span>
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-blue-400" />
            <FormattedMessage id="viewings.detail.inspectorHome" />
          </span>
        </div>
      </div>
    </section>
  );
}

function NotesPanel({ request }: { request: ViewingsDispatchRequest }) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-muted-foreground">
        <FormattedMessage id="viewings.detail.notes" />
      </p>
      <div className="min-h-20 rounded-md border border-border bg-muted-background p-3 text-sm text-primary">
        {request.specialNotes || "-"}
      </div>
    </div>
  );
}

function DetailStatusBanner({ request }: { request: ViewingsDispatchRequest }) {
  const intl = useIntl();
  const status = getLifecycleStatusLabel(intl, request.lifecycleStatus);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted-background px-4 py-3 text-sm text-primary">
      <p className="font-medium">
        <FormattedMessage id="viewings.table.status" />: {status}
      </p>
      <span className={getLifecycleBadgeClassName(request.lifecycleStatus)}>
        {status}
      </span>
    </div>
  );
}

function DetailHeader({ request }: { request: ViewingsDispatchRequest }) {
  const productType = request.productType || "-";

  return (
    <SheetHeader
      data-testid="viewing-detail-drawer-header"
      className="flex h-[52px] shrink-0 flex-row items-center justify-between space-y-0 border-b border-border px-4 text-left"
    >
      <div className="flex items-center gap-4">
        <span className="rounded-md border border-border p-2 text-xs font-medium text-primary">
          {productType}
        </span>
        <SheetTitle className="text-base font-semibold text-primary">
          {productType}
        </SheetTitle>
        <SheetDescription className="sr-only">
          <FormattedMessage id="viewings.detail.property" />
        </SheetDescription>
      </div>
      <SheetClose className="flex size-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-primary">
        <Maximize2 className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only">
          <FormattedMessage id="viewings.detail.close" />
        </span>
      </SheetClose>
    </SheetHeader>
  );
}

function PropertyFacts({ request }: { request: ViewingsDispatchRequest }) {
  const intl = useIntl();
  const scheduledFor = "scheduledFor" in request ? request.scheduledFor : null;
  const details = [
    [
      intl.formatMessage({ id: "viewings.detail.requestId" }),
      request.evalionId,
    ],
    [intl.formatMessage({ id: "viewings.table.address" }), request.addressLine],
    [
      intl.formatMessage({ id: "viewings.detail.productType" }),
      request.productType || "-",
    ],
    [
      intl.formatMessage({ id: "viewings.table.inspection" }),
      request.inspectionType
        ? getInspectionTypeLabel(intl, request.inspectionType)
        : "-",
    ],
    [
      intl.formatMessage({ id: "viewings.detail.scheduledDate" }),
      formatDate(scheduledFor),
    ],
    [
      intl.formatMessage({ id: "viewings.table.deadline" }),
      formatDate(request.deadlineDate),
    ],
    [intl.formatMessage({ id: "viewings.detail.customer" }), request.ownerName],
  ];

  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <FormattedMessage id="viewings.detail.property" />
      </h3>
      <dl className="space-y-3 text-sm">
        {details.map(([label, value]) => (
          <div key={label} className="grid grid-cols-[112px_1fr] gap-4">
            <dt className="flex items-center gap-1 text-muted-foreground">
              <span>{label}</span>
            </dt>
            <dd className="font-semibold text-primary">
              {label ===
              intl.formatMessage({ id: "viewings.table.inspection" }) ? (
                <Badge variant="outline" className="font-normal">
                  {value}
                </Badge>
              ) : (
                value
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function formatDate(value: unknown): string {
  if (typeof value !== "string" || !value.includes("-")) return "-";
  const [year, month, day] = value.slice(0, 10).split("-");

  return `${day}.${month}.${year}`;
}

function DetailOverview({
  request,
}: {
  request: ViewingsDispatchRequest & {
    latitude?: number | null;
    longitude?: number | null;
  };
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="space-y-4">
        <PropertyFacts request={request} />
        <NotesPanel request={request} />
      </div>
      <LocationPanel request={request} />
    </div>
  );
}

function InspectorFilesCard({
  request,
  activeInspector,
  onUploadFiles,
  onDeleteFile,
}: {
  request: ViewingsDispatchRequest & {
    files?: InspectorPortalUploadedFile[];
  };
  activeInspector?: ViewingsDispatchInspector;
  onUploadFiles: (requestId: string, files: File[]) => Promise<number>;
  onDeleteFile: (fileId: number) => Promise<void>;
}) {
  const intl = useIntl();
  const files = request.files ?? [];
  const [downloadingFileIds, setDownloadingFileIds] = useState<number[]>([]);
  const [openFileOptionsId, setOpenFileOptionsId] = useState<number | null>(
    null,
  );
  const canUpload =
    request.lifecycleStatus === "scheduled" &&
    request.assignedInspectorId === activeInspector?.id;
  const filesDownloading = downloadingFileIds.length > 0;

  const clearDownloadingFileIds = (fileIds: number[]) => {
    window.setTimeout(() => {
      setDownloadingFileIds((current) =>
        current.filter((fileId) => !fileIds.includes(fileId)),
      );
    }, 700);
  };

  const saveBlob = (blob: Blob, fileName: string) => {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  const downloadFile = async (file: InspectorPortalUploadedFile) => {
    setOpenFileOptionsId(null);
    setDownloadingFileIds((current) =>
      Array.from(new Set([...current, file.id])),
    );
    const blob = await apiCalls.downloadInspectorPortalAssignmentFile(
      request.id,
      file.id,
    );
    saveBlob(blob, file.originalName);
    clearDownloadingFileIds([file.id]);
  };

  const downloadAllFiles = async () => {
    const fileIds = files.map((file) => file.id);
    setOpenFileOptionsId(null);
    setDownloadingFileIds((current) =>
      Array.from(new Set([...current, ...fileIds])),
    );

    for (const file of files) {
      const blob = await apiCalls.downloadInspectorPortalAssignmentFile(
        request.id,
        file.id,
      );
      saveBlob(blob, file.originalName);
    }

    clearDownloadingFileIds(fileIds);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 border-b border-border p-4">
        <CardTitle className="text-base font-semibold text-primary">
          <FormattedMessage id="viewings.detail.inspectorFiles" />
        </CardTitle>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {files.length > 0 ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="gap-2"
              disabled={filesDownloading}
              onClick={() => void downloadAllFiles()}
            >
              {filesDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4" aria-hidden="true" />
              )}
              <FormattedMessage id="actions.downloadAll" />
            </Button>
          ) : null}
          {canUpload ? (
            <InspectorUploadDialog
              requestId={request.id}
              onUploadFiles={onUploadFiles}
            />
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {files.length > 0 ? (
          <ul className="space-y-3">
            {files.map((file) => {
              const fileIsDownloading = downloadingFileIds.includes(file.id);

              return (
                <li
                  key={file.id}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <FileIcon
                      className="h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <span className="truncate text-sm text-primary">
                      {file.originalName}
                    </span>
                  </div>
                  {fileIsDownloading ? (
                    <div
                      className="flex size-10 shrink-0 items-center justify-center text-muted-foreground"
                      aria-label={`Downloading ${file.originalName}`}
                    >
                      <Loader2
                        className="h-5 w-5 animate-spin"
                        aria-hidden="true"
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() =>
                          setOpenFileOptionsId((current) =>
                            current === file.id ? null : file.id,
                          )
                        }
                        aria-label={intl.formatMessage(
                          { id: "actions.fileOptions" },
                          { name: file.originalName },
                        )}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                      {openFileOptionsId === file.id ? (
                        <div
                          data-testid="file-options-menu"
                          className="absolute right-0 top-11 z-50 w-72 rounded-md border bg-popover p-0 text-popover-foreground shadow-md"
                        >
                          <div className="border-b border-border px-4 py-3 font-semibold">
                            <FormattedMessage id="actions.options" />
                          </div>
                          <div className="flex flex-col">
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-12 justify-start gap-3 rounded-none px-4"
                              onClick={() => void downloadFile(file)}
                            >
                              <Download
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                              <FormattedMessage id="actions.downloadFile" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-12 justify-start gap-3 rounded-none px-4"
                              onClick={() =>
                                void navigator.clipboard.writeText(
                                  file.signedUrl,
                                )
                              }
                            >
                              <Link2 className="h-4 w-4" aria-hidden="true" />
                              <FormattedMessage id="actions.copyLink" />
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              className="m-2 h-10 justify-start gap-3"
                              onClick={() => void onDeleteFile(file.id)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                              <FormattedMessage id="actions.deleteFileOption" />
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            <FormattedMessage id="viewings.detail.noInspectorFiles" />
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PendingOfferContent({
  request,
  activeInspector,
  acceptDateInput,
  actionErrorMessageId,
  onAcceptDateInputChange,
  onUpdateRequest,
  onAcceptOffer,
  onDeclineOffer,
  onSubmitViewingDate,
  onUploadFiles,
}: {
  request: ViewingsDispatchRequest & { version?: number };
  activeInspector?: ViewingsDispatchInspector;
  acceptDateInput: string;
  actionErrorMessageId?: string | null;
  onAcceptDateInputChange: (value: string) => void;
  onUpdateRequest: (requestId: string, updater: RequestUpdater) => void;
  onAcceptOffer: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onDeclineOffer: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onSubmitViewingDate: (
    request: ViewingsDispatchRequest & { version?: number },
    scheduledFor: string,
  ) => void;
  onUploadFiles: (requestId: string, files: File[]) => Promise<number>;
}) {
  const intl = useIntl();
  const inspectionLabel = request.inspectionType
    ? getInspectionTypeLabel(intl, request.inspectionType)
    : "-";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
        <section
          data-testid="pending-offer-decision-card"
          className="border-b border-border pb-3"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <FormattedMessage id="viewings.offer.pendingBadge" />
          </p>
          <h3 className="mt-1 text-base font-semibold text-primary">
            <FormattedMessage id="viewings.offer.decisionTitle" />
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            <FormattedMessage id="viewings.offer.decisionDescription" />
          </p>
        </section>

        {shouldShowWaitingOver48hAlert(request) ? (
          <WaitingOver48hAlertSection />
        ) : null}

        <section
          data-testid="pending-offer-details"
          className="rounded-md border border-border bg-card p-3"
        >
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <FormattedMessage id="viewings.offer.details" />
          </h3>
          <dl className="grid gap-2 sm:grid-cols-2">
            <SimpleFact
              label={<FormattedMessage id="viewings.detail.requestId" />}
              value={request.evalionId}
            />
            <SimpleFact
              label={<FormattedMessage id="viewings.offer.where" />}
              value={request.addressLine}
              prominent
            />
            <SimpleFact
              label={<FormattedMessage id="viewings.table.inspection" />}
              value={inspectionLabel}
            />
            <SimpleFact
              label={<FormattedMessage id="viewings.table.distance" />}
              value={
                request.distanceKm === undefined
                  ? "-"
                  : `${request.distanceKm} km`
              }
            />
            <SimpleFact
              label={<FormattedMessage id="viewings.offer.replyDeadline" />}
              value={request.deadlineDate || "-"}
            />
            <SimpleFact
              label={<FormattedMessage id="viewings.table.product" />}
              value={request.productType || "-"}
            />
            <SimpleFact
              label={<FormattedMessage id="viewings.offer.notes" />}
              value={request.specialNotes || "-"}
            />
          </dl>
        </section>
      </div>

      <div
        data-testid="pending-offer-actions-footer"
        className="mt-auto border-t border-border bg-card px-6 py-3"
      >
        <AssignmentActions
          request={request}
          activeInspector={activeInspector}
          acceptDateInput={acceptDateInput}
          onAcceptDateInputChange={onAcceptDateInputChange}
          onUpdateRequest={onUpdateRequest}
          onAcceptOffer={onAcceptOffer}
          onDeclineOffer={onDeclineOffer}
          onSubmitViewingDate={onSubmitViewingDate}
          onUploadFiles={onUploadFiles}
          presentation="decision"
        />
        {actionErrorMessageId ? (
          <p className="mt-2 text-sm text-destructive">
            <FormattedMessage id={actionErrorMessageId} />
          </p>
        ) : null}
      </div>
    </div>
  );
}

function DetailContent({
  request,
  activeInspector,
  activeDetailTab,
  acceptDateInput,
  actionErrorMessageId,
  onDetailTabChange,
  onAcceptDateInputChange,
  onUpdateRequest,
  onAcceptOffer,
  onDeclineOffer,
  onSubmitViewingDate,
  onUploadFiles,
  onDeleteFile,
}: {
  request: ViewingsDispatchRequest & {
    latitude?: number | null;
    longitude?: number | null;
    files?: InspectorPortalUploadedFile[];
  };
  activeInspector?: ViewingsDispatchInspector;
  activeDetailTab: AssignmentDetailTab;
  acceptDateInput: string;
  actionErrorMessageId?: string | null;
  onDetailTabChange: (value: AssignmentDetailTab) => void;
  onAcceptDateInputChange: (value: string) => void;
  onUpdateRequest: (requestId: string, updater: RequestUpdater) => void;
  onAcceptOffer: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onDeclineOffer: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onSubmitViewingDate: (
    request: ViewingsDispatchRequest & { version?: number },
    scheduledFor: string,
  ) => void;
  onUploadFiles: (requestId: string, files: File[]) => Promise<number>;
  onDeleteFile: (requestId: string, fileId: number) => Promise<void>;
}) {
  const isPendingOffer = OFFER_OPEN_STATUSES.includes(request.lifecycleStatus);

  if (isPendingOffer) {
    return (
      <PendingOfferContent
        request={request}
        activeInspector={activeInspector}
        acceptDateInput={acceptDateInput}
        actionErrorMessageId={actionErrorMessageId}
        onAcceptDateInputChange={onAcceptDateInputChange}
        onUpdateRequest={onUpdateRequest}
        onAcceptOffer={onAcceptOffer}
        onDeclineOffer={onDeclineOffer}
        onSubmitViewingDate={onSubmitViewingDate}
        onUploadFiles={onUploadFiles}
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto px-6 py-6">
      <Tabs
        value={activeDetailTab}
        onValueChange={(value) =>
          onDetailTabChange(value === "files" ? "files" : "info")
        }
      >
        <TabsList className="bg-muted-background">
          <TabsTrigger value="info" className="data-[state=active]:bg-white">
            <FormattedMessage id="viewings.detail.info" />
          </TabsTrigger>
          <TabsTrigger value="files" className="data-[state=active]:bg-white">
            <FormattedMessage id="viewings.detail.files" />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-0">
          <div className="flex flex-col gap-5 pt-6">
            <DetailStatusBanner request={request} />
            {shouldShowWaitingOver48hAlert(request) ? (
              <WaitingOver48hAlertSection />
            ) : null}
            <DetailOverview request={request} />
          </div>
        </TabsContent>

        <TabsContent value="files" className="mt-0">
          <div className="pt-6">
            <InspectorFilesCard
              request={request}
              activeInspector={activeInspector}
              onUploadFiles={onUploadFiles}
              onDeleteFile={(fileId) => onDeleteFile(request.id, fileId)}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function ViewingDetailSheet({
  request,
  inspectorId,
  activeInspector,
  activeDetailTab,
  acceptDateInput,
  actionErrorMessageId,
  onOpenChange,
  onDetailTabChange,
  onAcceptDateInputChange,
  onUpdateRequest,
  onAcceptOffer,
  onDeclineOffer,
  onSubmitViewingDate,
  onUploadFiles,
  onDeleteFile,
}: {
  request: InspectorPortalAssignmentSummary | null;
  inspectorId: string;
  activeInspector?: ViewingsDispatchInspector;
  activeDetailTab: AssignmentDetailTab;
  acceptDateInput: string;
  actionErrorMessageId?: string | null;
  onOpenChange: (open: boolean) => void;
  onDetailTabChange: (value: AssignmentDetailTab) => void;
  onAcceptDateInputChange: (value: string) => void;
  onUpdateRequest: (requestId: string, updater: RequestUpdater) => void;
  onAcceptOffer: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onDeclineOffer: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onSubmitViewingDate: (
    request: ViewingsDispatchRequest & { version?: number },
    scheduledFor: string,
  ) => void;
  onUploadFiles: (requestId: string, files: File[]) => Promise<number>;
  onDeleteFile: (requestId: string, fileId: number) => Promise<void>;
}) {
  const detailQuery = useGetInspectorPortalAssignmentDetail(request?.id);
  const detailAssignment = detailQuery.data?.data.assignment;
  const displayRequest = detailAssignment
    ? {
        ...toViewingRequest(
          {
            ...detailAssignment,
            ...request,
            customerLabel: detailAssignment.customerLabel,
            deadlineDate: detailAssignment.deadlineDate,
            dispatcherContact: detailAssignment.dispatcherContact,
            inspectorInstructions: detailAssignment.inspectorInstructions,
            approximateDistanceKm: detailAssignment.approximateDistanceKm,
          },
          inspectorId,
        ),
        version: request?.version,
        scheduledFor: detailAssignment.scheduledFor,
        latitude: detailAssignment.property.latitude,
        longitude: detailAssignment.property.longitude,
        files: detailAssignment.files,
      }
    : request
      ? {
          ...toViewingRequest(request, inspectorId),
          version: request.version,
          scheduledFor: request.scheduledFor,
        }
      : null;
  const isUnavailable = isAxiosError(detailQuery.error)
    ? [403, 404].includes(detailQuery.error.response?.status ?? 0)
    : false;
  const isPendingOffer = displayRequest
    ? OFFER_OPEN_STATUSES.includes(displayRequest.lifecycleStatus)
    : false;

  return (
    <Sheet open={Boolean(request)} onOpenChange={onOpenChange}>
      {request ? (
        <SheetContent
          data-testid="selected-viewing-detail"
          hideCloseIcon
          className={`flex w-full flex-col gap-0 overflow-hidden bg-card p-0 ${
            isPendingOffer
              ? "sm:max-w-[52rem]"
              : "w-[30vw] max-w-[30vw] sm:w-[30vw] sm:max-w-[30vw] sm:max-w-none"
          }`}
        >
          {detailQuery.isLoading ? (
            <>
              {displayRequest ? (
                <DetailHeader request={displayRequest} />
              ) : null}
              <div className="p-6 text-sm text-muted-foreground">
                <FormattedMessage id="viewings.detail.loading" />
              </div>
            </>
          ) : isUnavailable ? (
            <>
              {displayRequest ? (
                <DetailHeader request={displayRequest} />
              ) : null}
              <div className="p-6 text-sm text-muted-foreground">
                <FormattedMessage id="viewings.detail.unavailable" />
              </div>
            </>
          ) : detailQuery.isError || !displayRequest ? (
            <>
              {displayRequest ? (
                <DetailHeader request={displayRequest} />
              ) : null}
              <div className="p-6 text-sm text-muted-foreground">
                <FormattedMessage id="viewings.detail.error" />
              </div>
            </>
          ) : (
            <>
              <DetailHeader request={displayRequest} />
              <DetailContent
                request={displayRequest}
                activeInspector={activeInspector}
                activeDetailTab={activeDetailTab}
                acceptDateInput={acceptDateInput}
                actionErrorMessageId={actionErrorMessageId}
                onDetailTabChange={onDetailTabChange}
                onAcceptDateInputChange={onAcceptDateInputChange}
                onUpdateRequest={onUpdateRequest}
                onAcceptOffer={onAcceptOffer}
                onDeclineOffer={onDeclineOffer}
                onSubmitViewingDate={onSubmitViewingDate}
                onUploadFiles={onUploadFiles}
                onDeleteFile={onDeleteFile}
              />
            </>
          )}
        </SheetContent>
      ) : null}
    </Sheet>
  );
}
